import React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import MoreVertIcon from "@mui/icons-material/MoreVert";


/**
 * Three-dot menu for a single membership row in the user-detail drawer.
 * Each menu item dispatches a request to open one of the existing bulk
 * modals, pre-scoped to {this user × this store}. This avoids reinventing
 * single-store action UIs — the bulk modals already validate/permission/
 * cascade correctly, and admins can still adjust the scope inside the
 * modal if they want to widen it.
 *
 * Props:
 *   stm: the membership row {stm_id, store_id, store_name, is_archived, ...}
 *   userId: the parent user_id
 *   applyToOthersAvailable: true iff the user has at least one OTHER active
 *     STM in this company (i.e. there's somewhere to apply this row to).
 *     When false, the "Apply to other stores" items are hidden.
 *   onAction: (action, payload) => void
 *     action: "permission" | "overrides" | "archive" | "reactivate"
 *           | "apply_permission" | "apply_overrides"
 *     payload: { userId, storeId, sourcePermission?, sourceOverrides? }
 */
export default function MembershipActionMenu({ stm, userId, applyToOthersAvailable, onAction }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const dispatch = (action) => () => {
    close();
    onAction?.(action, {
      userId,
      storeId: stm.store_id,
      sourcePermission: stm.permission_type,
      sourceOverrides: stm.permission_type_overrides || [],
    });
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label={`Actions for ${stm.store_name}`}
        sx={{ p: 0.5 }}
      >
        <MoreVertIcon style={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 200,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {stm.is_archived ? (
          <MenuItem onClick={dispatch("reactivate")} sx={{ py: 0.75 }}>
            <ListItemText
              primaryTypographyProps={{ fontSize: 13, color: "#334155" }}
            >
              Reactivate at this store
            </ListItemText>
          </MenuItem>
        ) : (
          <>
            <MenuItem onClick={dispatch("permission")} sx={{ py: 0.75 }}>
              <ListItemText
                primaryTypographyProps={{ fontSize: 13, color: "#334155" }}
              >
                Change permission
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={dispatch("overrides")} sx={{ py: 0.75 }}>
              <ListItemText
                primaryTypographyProps={{ fontSize: 13, color: "#334155" }}
              >
                Change special access
              </ListItemText>
            </MenuItem>
            {applyToOthersAvailable && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={dispatch("apply_permission")} sx={{ py: 0.75 }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 13, color: "#334155" }}
                    secondaryTypographyProps={{ fontSize: 11, color: "#94a3b8" }}
                    secondary="Copy this permission to all their other active stores"
                  >
                    Apply permission to other stores
                  </ListItemText>
                </MenuItem>
                <MenuItem onClick={dispatch("apply_overrides")} sx={{ py: 0.75 }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: 13, color: "#334155" }}
                    secondaryTypographyProps={{ fontSize: 11, color: "#94a3b8" }}
                    secondary="Replace special access at all their other active stores with this row's set"
                  >
                    Apply special access to other stores
                  </ListItemText>
                </MenuItem>
              </>
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={dispatch("archive")} sx={{ py: 0.75 }}>
              <ListItemText
                primaryTypographyProps={{ fontSize: 13, color: "#dc2626" }}
              >
                Archive at this store
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
